import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { backofficeClient } from "@/lib/api/backoffice-client";
import type { ClassifiableEmployee } from "@/lib/solution-engineer/service-accounts";

/** employees service, via the backoffice proxy. */
const EMPLOYEES_PATH = "/proxy/employees/api/v1/employees/";
const BATCH_UPDATE_PATH = "/proxy/employees/api/v1/employees/batch_update";

const PAGE_SIZE = 1000;
const MAX_PAGES = 50;
/** employees MAX_BATCH_ITEMS. */
const MAX_BATCH = 250;

interface EmployeesPage {
  total_count: number;
  items: ClassifiableEmployee[];
}

/** Mirrors employees EmployeeBatchUpdateResult. */
interface BatchUpdateResult {
  employee: { id: string } | null;
  error: string | null;
}

export interface MarkResult {
  updated: string[];
  failed: { id: string; error: string }[];
}

const employeesQueryKey = (tenantId: string) =>
  ["solution-engineer", "employees", tenantId] as const;

/** Every synced employee of the tenant, people and service accounts alike. */
export function useTenantEmployees(tenantId: string) {
  return useQuery({
    queryKey: employeesQueryKey(tenantId),
    queryFn: async () => {
      const employees: ClassifiableEmployee[] = [];
      for (let page = 0; page < MAX_PAGES; page++) {
        const { data } = await backofficeClient.get<EmployeesPage>(
          EMPLOYEES_PATH,
          {
            params: {
              tenant_id: tenantId,
              limit: PAGE_SIZE,
              offset: page * PAGE_SIZE,
            },
          },
        );
        employees.push(...data.items);
        if (data.items.length < PAGE_SIZE || employees.length >= data.total_count) {
          break;
        }
      }
      return employees;
    },
    enabled: Boolean(tenantId),
  });
}

/**
 * Marks employees as service accounts in batches of the API's cap. The API
 * reports per-item failures, so one bad id does not fail the rest.
 */
export function useMarkServiceAccounts(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]): Promise<MarkResult> => {
      const result: MarkResult = { updated: [], failed: [] };
      for (let start = 0; start < ids.length; start += MAX_BATCH) {
        const batch = ids.slice(start, start + MAX_BATCH);
        const { data } = await backofficeClient.post<{
          results: BatchUpdateResult[];
        }>(
          BATCH_UPDATE_PATH,
          { updates: batch.map((id) => ({ id, type: "service_account" })) },
          { params: { tenant_id: tenantId } },
        );
        // Results come back in the order of the updates
        batch.forEach((id, index) => {
          const item = data.results.at(index);
          if (item?.employee) result.updated.push(id);
          else result.failed.push({ id, error: item?.error ?? "No result returned" });
        });
      }
      return result;
    },
    // Also after a failure: earlier batches may have gone through
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: employeesQueryKey(tenantId),
      });
    },
  });
}
